import React from 'react'
import { Eye, EyeOff, LucideIcon } from 'lucide-react'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode
  error?: string
  helperText?: string
  icon?: LucideIcon
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', label, error, helperText, type = 'text', icon: Icon, ...props }, ref) => {
    const inputId = React.useId()
    const [showPassword, setShowPassword] = React.useState(false)

    const isPasswordType = type === 'password'
    const inputType = isPasswordType && showPassword ? 'text' : type

    return (
      <div className="space-y-2">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-bold text-text-secondary uppercase tracking-widest"
          >
            {label}
          </label>
        )}
        <div className="relative group">
          {Icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary group-focus-within:text-primary transition-colors duration-200">
              <Icon size={18} />
            </div>
          )}
          <input
            id={inputId}
            type={inputType}
            className={`
              flex h-12 w-full rounded-xl border border-white/10 bg-white/5 py-2 text-sm text-white font-medium
              ring-offset-app-bg file:border-0 file:bg-transparent file:text-sm file:font-medium 
              placeholder:text-text-secondary/50 focus-visible:outline-none focus-visible:ring-2 
              focus-visible:ring-primary/50 focus-visible:border-primary/60 transition-all duration-200
              hover:border-white/20 hover:bg-white/[0.07]
              disabled:cursor-not-allowed disabled:bg-white/[0.02] disabled:text-text-secondary/50
              ${Icon ? 'pl-10' : 'px-4'}
              ${error ? 'border-error/50 ring-error/30 focus-visible:ring-error/50 focus-visible:border-error/60' : ''}
              ${isPasswordType ? 'pr-10' : ''}
              ${className}
            `}
            ref={ref}
            {...props}
          />
          {isPasswordType && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff size={18} />
              ) : (
                <Eye size={18} />
              )}
            </button>
          )}
        </div>
        {error && (
          <p className="text-sm font-medium text-error flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
            <span className="w-1 h-1 rounded-full bg-error" />
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-xs text-text-secondary/70">{helperText}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }